import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Archive,
  ExternalLink,
  RefreshCw,
  RotateCcw,
  Search,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (value) => {
  if (!value) return "Unknown";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

export const ArchivedProjectsAdminPage = () => {
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);

  const load = async () => {
    setLoading(true);

    try {
      const response = await axios.get(
        `${API}/project-archive`
      );

      setProjects(response.data?.projects || []);
    } catch (error) {
      toast.error(
        error.response?.data?.detail
          || "Failed to load archived projects"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();

    if (!needle) return projects;

    return projects.filter((project) => {
      const values = [
        project.name,
        project.project_number,
        project.customer_name,
        project.manufacturer_name,
        project.status,
        project.workflow_phase,
        project.archived_by,
      ];

      return values.some(
        (value) => String(value || "").toLowerCase().includes(needle)
      );
    });
  }, [projects, search]);

  const restore = async (project) => {
    if (
      !window.confirm(
        `Restore "${project.name || "this project"}" to its prior workflow stage?`
      )
    ) {
      return;
    }

    setRestoringId(project.id);

    try {
      await axios.post(
        `${API}/project-archive/${project.id}/restore`
      );

      toast.success("Project restored");

      setProjects((current) =>
        current.filter((item) => item.id !== project.id)
      );
    } catch (error) {
      toast.error(
        error.response?.data?.detail
          || "Failed to restore project"
      );
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <div
      className="animate-fade-in"
      data-testid="graphics-admin-project-archive-v30"
    >
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Archive size={28} />
            <h1 className="text-3xl font-bold">
              Archived Projects
            </h1>
          </div>

          <p className="mt-2 text-muted-foreground">
            Graphics Admin archive. Files, notes, approvals, and
            activity history remain attached to every project.
          </p>
        </div>

        <button
          type="button"
          onClick={load}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-secondary px-4 py-2.5 font-medium"
        >
          <RefreshCw size={17} />
          Refresh
        </button>
      </div>

      <div className="relative mb-6 max-w-xl">
        <Search
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />

        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search archived projects..."
          className="w-full rounded-lg border border-border bg-secondary py-2.5 pl-10 pr-4"
        />
      </div>

      {loading ? (
        <div className="text-muted-foreground">
          Loading archived projects...
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-10 text-center">
          <Archive
            size={40}
            className="mx-auto mb-3 opacity-40"
          />

          <p className="font-medium">
            No archived projects found
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-border bg-secondary/50 text-left text-sm">
                <tr>
                  <th className="px-4 py-3">Project</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Prior Stage</th>
                  <th className="px-4 py-3">Archived</th>
                  <th className="px-4 py-3">Archived By</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((project) => (
                  <tr
                    key={project.id}
                    className="border-b border-border last:border-0"
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium">
                        {project.name || "Unnamed Project"}
                      </p>

                      {project.project_number && (
                        <p className="text-xs text-muted-foreground">
                          {project.project_number}
                        </p>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      {project.customer_name || "Unassigned"}
                    </td>

                    <td className="px-4 py-3">
                      {project.archived_from_workflow_phase
                        || project.archived_from_status
                        || project.workflow_phase
                        || project.status
                        || "Unknown"}
                    </td>

                    <td className="px-4 py-3 text-sm">
                      {formatDate(project.archived_at)}
                    </td>

                    <td className="px-4 py-3">
                      {project.archived_by || "Graphics Admin"}
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Link
                          to={`/projects/${project.id}`}
                          className="inline-flex items-center gap-1 rounded-lg bg-secondary px-3 py-2 text-sm"
                        >
                          <ExternalLink size={15} />
                          Open
                        </Link>

                        <button
                          type="button"
                          onClick={() => restore(project)}
                          disabled={restoringId === project.id}
                          className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
                        >
                          <RotateCcw size={15} />
                          {restoringId === project.id
                            ? "Restoring..."
                            : "Restore"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
